<template>
    <div>
        <v-row
            v-for="(row, indx) in rows"
            :key="'r' + indx"
            align="center"
            no-gutters
            class="my-3"
        >
            <template>
                <v-col
                    cols="3"
                    class="body-2 grey--text text--darken-1 text-sm-left"
                    v-if="showLabels"
                >
                    {{ row.label }}
                </v-col>
                <v-col
                    class="text-sm-left"
                >
                    <div v-if="row.isGoogle" style="max-width: 400px">
                        <LoginIntegrationButton
                            v-if="!isGoogleSetup"
                            :integration-type="auth.AUTH_METHODS.GOOGLE"
                            :show-disable-text="Boolean(current_account.google_id)"
                            :show-connect-text="!current_account.google_id"
                            @click="onConnectGoogle"
                        />
                        <div v-else id="google-signin"></div>
                    </div>
                    <div v-else-if="row.isEmail" style="max-width: 400px">
                        <v-btn
                            :loading="loading"
                            color="primary"
                            class="white--text"
                            block
                            @click="row.click()"
                        >
                            <v-row no-gutters align="center">
                                <v-col></v-col>
                                <v-col>
                                    <span>{{ row.label }}</span>
                                </v-col>
                                <v-col class="text-right">
                                    <v-icon right>mdi-send</v-icon>
                                </v-col>
                            </v-row>
                        </v-btn>
                    </div>
                    <div
                        v-else-if="row.component"
                        :style="row.maxWidth ? `max-width: ${row.maxWidth}px` : ''"
                    >
                        <component
                            :is="row.component"
                            v-bind="row.props"
                            @click="row.click ? row.click($event) : null"
                        />
                    </div>
                </v-col>
            </template>
        </v-row>
    </div>
</template>
<script>
import Vue from "vue";
import {computed, ref} from "@vue/composition-api";
import {$t} from "@/i18n";
import AccountModule from "@/store/modules/account";
import DialogModule from "@/store/modules/dialog";
import auth from "@/modules/auth/auth";
import GoogleLogin from "@/composables/GoogleLogin";
import {startLoginProcess} from "@/modules/auth/microsoftAuth";
import AppModule from "@/store/modules/app"
import LoginIntegrationButton from '../../components/Buttons/LoginIntegrationButton.vue'
import ApiConnect from "@/api/account_setup/connect";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import authButton from "@/config/auth/authButton";
import ApiAuthRecovery from "@/api/auth/authRecovery";
import FeedbackNotificationModule from "@/store/modules/feedback_notification";


export default {
    name: "ExternalConnectionsBlock",
    components: {
        LoginIntegrationButton
    },
    props: {
        personId: {
            type: Number,
            required: true
        },
        googleAllowed: {
            type: Boolean,
            required: true
        },
        microsoftAllowed: {
            type: Boolean,
            required: true
        },
        xeroAllowed: {
            type: Boolean,
            required: false,
            default: false
        },
        emailAllowed: {
            type: Boolean,
            required: false,
            default: false
        },
        showLabels: {
            type: Boolean,
            required: false,
            default: true
        }
    },
    setup(props) {
        const isGoogleSetup = ref(false)
        const dialogModule = DialogModule();
        const appModule = AppModule();
        const {promptGoogleToken} = GoogleLogin();
        const { showFeedbackNotification } = FeedbackNotificationModule();
        const loading = ref(false)

        const onConnectGoogle = async () => {
           if (current_account.value.google_id) {
            dialogModule.setConfirmOptions({
                title: $t("Are you sure to remove google?"),
                confirm_text: $t("Delete"),
                cancel_text: $t("Cancel"),
                action: async () => {
                    try {
                        await ApiConnect.removeGoogleId()
                        current_account.value.google_id = null
                    } catch (e) {
                        errorDialog.showError(e)
                    }
                }
            });
            dialogModule.openConfirmDialog();
        } else {
            try {
                isGoogleSetup.value = true;
                await Vue.nextTick()
                promptGoogleToken(async (data) => {
                    try {
                        await ApiConnect.connectGoogleId(data.credential)
                        current_account.value.google_id = data.credential
                        isGoogleSetup.value = false
                    } catch (e) {
                        errorDialog.showError(e)
                    }
                }, 'google-signin')

            } catch (e) {
                errorDialog.showError(e)
            }
        }
        }

        const rows = computed(() => [
            props.googleAllowed ? {
                label: $t('Google log in'),
                component: LoginIntegrationButton,
                maxWidth: 400,
                props: {
                    sso_hint: current_account.value.email,
                    integrationType: auth.AUTH_METHODS.GOOGLE,
                    showDisableText: Boolean(current_account.value.google_id),
                    showConnectText: true
                },
                isGoogle: true
            } : null,
            props.microsoftAllowed ? {
                label: $t('Microsoft log in'),
                component: LoginIntegrationButton,
                maxWidth: 400,
                props: {
                    sso_hint: current_account.value.email,
                    integrationType: auth.AUTH_METHODS.MICROSOFT,
                    showDisableText: Boolean(current_account.value.microsoft_id),
                    showConnectText: true
                },
                click: async () => {
                    if (current_account.value.microsoft_id) {
                        dialogModule.setConfirmOptions({
                            title: $t('Are you sure to remove microsoft?'),
                            confirm_text: $t('Delete'),
                            cancel_text: $t('Cancel'),
                            action: async () => {
                                try {
                                    await ApiConnect.removeMicrosoftId()
                                    current_account.value.microsoft_id = null
                                } catch (e) {
                                    errorDialog.showError(e)
                                }
                            }
                        })
                        dialogModule.openConfirmDialog()
                    } else {
                        try {
                            const loginResponse = await startLoginProcess(appModule.azure_ad_app_id.value, current_account.value.email)
                            if (loginResponse) {
                                await ApiConnect.connectMicrosoftId(loginResponse['idToken'])
                                current_account.value.microsoft_id = loginResponse['idToken']
                            }
                        } catch (e) {
                            errorDialog.showError(e)
                        }
                    }
                }
            } : null,
            props.xeroAllowed ? {
                label: authButton.XERO.label,
                component: LoginIntegrationButton,
                maxWidth: 400,
                props: {
                    integrationType: auth.AUTH_METHODS.XERO,
                    showDisableText: Boolean(current_account.value.xero_id),
                    showConnectText: true
                },
                click: async () => {
                    if (current_account.value.xero_id) {
                        dialogModule.setConfirmOptions({
                            title: $t('Are you want to disconnect Xero?'),
                            confirm_text: $t('Delete'),
                            cancel_text: $t('Cancel'),
                            action: async () => {
                                try {
                                    await ApiConnect.removeXeroId()
                                    current_account.value.xero_id = null
                                } catch (e) {
                                    errorDialog.showError(e)
                                }
                            }
                        })
                        dialogModule.openConfirmDialog()
                    } else {
                        ApiConnect.connectXeroId()
                    }
                }
            } : null,
            props.emailAllowed ? {
                label: $t('Reset password'),
                isEmail: true,
                click: async () => {
                    loading.value = true;
                    try {
                        await ApiAuthRecovery.sendPasswordReminder(current_account.value.email)
                        const msg = $t("Instructions for resetting your password have been sent to") + ' ' + current_account.value.email
                        showFeedbackNotification(msg)
                    } catch (e) {
                        errorDialog.showError(e)
                    }

                    loading.value = false;
                }
            } : null
        ].filter(x => x))

        const {
            current_account
        } = AccountModule();
        return {
            rows,
            isGoogleSetup,
            onConnectGoogle,
            current_account,
            auth,
            loading
        }
    }
}
</script>
<style scoped lang="scss">

</style>